import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["document", "documentContent", "marginColumn", "commentForm", "commentText", "selectedText", "commentsList", "emptyState", "agreeBadge", "partiallyAgreeBadge", "disagreeBadge", "allTab", "unresolvedTab", "resolvedTab", "saveButton"]
  static values = {
    inloopId: String,
    documentVersionId: String
  }

  connect() {
    // Listen for text selection
    this.boundHandleSelection = this.handleSelection.bind(this)
    document.addEventListener('mouseup', this.boundHandleSelection)

    // Listen for window resize to reposition buttons
    this.boundHandleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.boundHandleResize)

    // Listen for container scroll to reposition buttons
    this.boundHandleScroll = this.handleResize.bind(this) // Reuse resize handler
    if (this.hasDocumentTarget) {
      this.documentTarget.addEventListener('scroll', this.boundHandleScroll)
    }

    // Listen for tab changes to disable commenting in AI view
    this.boundHandleTabChange = this.handleTabChange.bind(this)
    document.addEventListener('tabs:tabChanged', this.boundHandleTabChange)

    // Initialize selected comment type
    this.selectedCommentType = null

    // Initialize active filter
    this.activeFilter = 'all'

    // Track if commenting is enabled (disabled in AI view)
    this.commentingEnabled = true

    // Store current selection range for highlighting
    this.currentRange = null
    this.currentHighlight = null
    this.savedSelectedText = null // Store text for message button after highlight removed
    this.savedTextNode = null // Store the text node after highlight removed
    this.commentId = null // Unique ID for linking comment with highlighted text

    // Store selection character offsets
    this.selectionStart = null
    this.selectionEnd = null

    // Store all message buttons (one per comment)
    this.messageButtons = [] // Array of { button, commentId, textNode, selectedText, highlight }

    // Track occupied positions to prevent overlap
    this.occupiedPositions = [] // Array of { top, bottom } ranges

    // Restore highlights for existing comments on page load
    this.restoreExistingCommentHighlights()
  }

  disconnect() {
    document.removeEventListener('mouseup', this.boundHandleSelection)
    window.removeEventListener('resize', this.boundHandleResize)
    document.removeEventListener('tabs:tabChanged', this.boundHandleTabChange)
    if (this.hasDocumentTarget) {
      this.documentTarget.removeEventListener('scroll', this.boundHandleScroll)
    }
    this.removeAllMessageButtons()
  }

  handleTabChange(event) {
    const activeTab = event.detail.activeTab

    // Disable commenting when AI view is active
    if (activeTab === 'ai') {
      this.commentingEnabled = false
      // Hide comment form if it's open
      this.hideCommentForm(false)
    } else {
      this.commentingEnabled = true
    }
  }

  handleSelection() {
    // Don't allow selection if commenting is disabled (AI view)
    if (!this.commentingEnabled) {
      return
    }

    const selection = window.getSelection()
    const selectedText = selection.toString().trim()

    // Check if text is selected and it's within the document content
    if (selectedText.length > 0) {
      const range = selection.getRangeAt(0)
      const container = range.commonAncestorContainer

      // Check if selection is within the document content area
      if (this.hasDocumentTarget && this.documentTarget.contains(container)) {

        // Store the range for highlighting
        this.currentRange = range.cloneRange()

        // Calculate and store character offsets
        const offsets = this.getCharacterOffsets(range)
        this.selectionStart = offsets.start
        this.selectionEnd = offsets.end

        // Apply highlight when text is selected
        this.highlightSelection()

        // Show comment form (message button will appear after comment is created)
        this.showCommentForm(selectedText)
      }
    }
  }

  handleResize() {
    // Debounce resize events to avoid excessive calculations
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }

    this.resizeTimeout = setTimeout(() => {
      this.repositionAllButtons()
    }, 150)
  }

  repositionAllButtons() {
    // Reposition buttons in margin column based on current text line positions
    const marginColumn = this.hasMarginColumnTarget ? this.marginColumnTarget : null
    if (!marginColumn) return

    const marginRect = marginColumn.getBoundingClientRect()

    // Clear occupied positions and recalculate
    this.occupiedPositions = []
    const buttonHeight = 32
    const minSpacing = 4

    this.messageButtons.forEach(buttonData => {
      try {
        // Use highlight if exists, otherwise use textNode
        const textNode = buttonData.highlight || buttonData.textNode
        if (!textNode) return

        // Get current line position
        const endOfLine = this.getEndOfLinePosition(textNode)
        if (!endOfLine) return

        // Calculate desired position and check for collision
        let topPos = endOfLine.top - marginRect.top
        topPos = this.findNonCollidingPosition(topPos, buttonHeight, minSpacing)

        // Update button position
        buttonData.button.style.top = `${topPos}px`

        // Track this position as occupied
        this.occupiedPositions.push({
          top: topPos,
          bottom: topPos + buttonHeight
        })
      } catch (error) {
        // Skip buttons that can't be repositioned
      }
    })
  }

  // Find a non-colliding position for a button
  findNonCollidingPosition(desiredTop, buttonHeight, minSpacing) {
    let currentTop = desiredTop
    let hasCollision = true

    // Keep checking until we find a non-colliding position
    while (hasCollision) {
      hasCollision = false

      // Check against all occupied positions
      for (const occupied of this.occupiedPositions) {
        const currentBottom = currentTop + buttonHeight

        // Check if there's overlap (with spacing buffer)
        if (currentTop < occupied.bottom + minSpacing &&
            currentBottom + minSpacing > occupied.top) {
          // Collision detected, move below this occupied space
          hasCollision = true
          currentTop = occupied.bottom + minSpacing
          break
        }
      }
    }

    return currentTop
  }

  showCommentForm(selectedText) {
    // Store the selected text
    if (this.hasSelectedTextTarget) {
      this.selectedTextTarget.textContent = selectedText
    }

    // Show the comment form in the sidebar
    if (this.hasCommentFormTarget) {
      this.commentFormTarget.classList.remove('hidden')

      // Focus on the comment textarea
      if (this.hasCommentTextTarget) {
        this.commentTextTarget.focus()
      }
    }

    // Dispatch event to notify that comment form is shown
    this.dispatch('formShown', {
      detail: { selectedText },
      bubbles: true
    })
  }

  hideCommentForm(keepMessageButton = false) {
    if (this.hasCommentFormTarget) {
      this.commentFormTarget.classList.add('hidden')
    }

    // Clear the comment text
    if (this.hasCommentTextTarget) {
      this.commentTextTarget.value = ''
    }

    // Reset selected comment type
    this.selectedCommentType = null
    this.resetBadgeSelection()

    // Always remove highlight when hiding form
    this.removeHighlight()

    // Only remove message button if not keeping it (e.g., on cancel)
    if (!keepMessageButton) {
      this.removeMessageButton()
    }

  }

  selectCommentType(event) {
    const badge = event.currentTarget
    const commentType = badge.dataset.commentType

    this.selectedCommentType = commentType

    // Reset all badges to outlined
    this.resetBadgeSelection()

    // Change selected badge to filled and remove opacity
    const outlinedBadge = badge.querySelector('[data-outlined-badge]')
    const filledBadge = badge.querySelector('[data-filled-badge]')

    if (outlinedBadge && filledBadge) {
      outlinedBadge.classList.add('hidden')
      filledBadge.classList.remove('hidden')
      badge.classList.remove('opacity-50')
      badge.dataset.badgeState = 'filled'
    }

    // Enable save button when comment type is selected
    this.updateSaveButtonState()
  }

  resetBadgeSelection() {
    // Reset all badges to outlined state
    const badges = [
      this.hasAgreeBadgeTarget ? this.agreeBadgeTarget : null,
      this.hasPartiallyAgreeBadgeTarget ? this.partiallyAgreeBadgeTarget : null,
      this.hasDisagreeBadgeTarget ? this.disagreeBadgeTarget : null
    ].filter(badge => badge !== null)

    badges.forEach(badge => {
      const outlinedBadge = badge.querySelector('[data-outlined-badge]')
      const filledBadge = badge.querySelector('[data-filled-badge]')

      if (outlinedBadge && filledBadge) {
        outlinedBadge.classList.remove('hidden')
        filledBadge.classList.add('hidden')
        badge.classList.add('opacity-50')
        badge.dataset.badgeState = 'outlined'
      }
    })

    // Disable save button when badges are reset
    this.updateSaveButtonState()
  }

  updateSaveButtonState() {
    if (!this.hasSaveButtonTarget) return

    const button = this.saveButtonTarget.querySelector('button')
    if (!button) return

    // Enable button if comment type is selected, disable otherwise
    if (this.selectedCommentType) {
      button.disabled = false
      button.classList.remove('opacity-50', 'cursor-not-allowed')
      button.classList.add('cursor-pointer')
    } else {
      button.disabled = true
      button.classList.add('opacity-50', 'cursor-not-allowed')
      button.classList.remove('cursor-pointer')
    }
  }

  async saveComment(event) {
    event.preventDefault()

    const selectedText = this.hasSelectedTextTarget ? this.selectedTextTarget.textContent : ''
    const commentText = this.hasCommentTextTarget ? this.commentTextTarget.value : ''

    if (commentText.trim() === '') {
      return
    }

    if (!this.selectedCommentType) {
      return
    }

    // Save selected text and get text node from current selection
    this.savedSelectedText = selectedText

    // Get the text node from the current range
    if (this.currentRange) {
      const startNode = this.currentRange.startContainer
      if (startNode.nodeType === Node.TEXT_NODE) {
        this.savedTextNode = startNode
      } else if (startNode.firstChild && startNode.firstChild.nodeType === Node.TEXT_NODE) {
        this.savedTextNode = startNode.firstChild
      }
    }

    // Create comment card and add it to the list
    const createdId = await this.createCommentCard({
      commentType: this.selectedCommentType,
      commentText: commentText,
      commenterName: 'Olivier Park', // TODO: Get from current user
      selectedText: this.savedSelectedText
    })
    if (!createdId) {
      return
    }
    this.commentId = createdId

    // Hide the form but keep message button
    this.hideCommentForm(true)

    // Hide empty state if it exists
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add('hidden')
    }

    // Show message button after comment is created
    this.showMessageButton()

    // Dispatch event with comment data
    this.dispatch('commentSaved', {
      detail: {
        selectedText,
        commentText,
        commentType: this.selectedCommentType
      },
      bubbles: true
    })
  }

  async createCommentCard({ commentType, commentText, commenterName, selectedText }) {
    if (!this.hasCommentsListTarget) {
      return
    }

    try {
      // Get inloop ID from data attribute or URL
      const inloopId = this.inloopIdValue || this.getInloopIdFromUrl()

      // Prepare comment data
      const commentData = {
        comment_type: commentType,
        comment_text: commentText,
        commenter_name: commenterName,
        selected_text: selectedText,
        selection_start: this.selectionStart,
        selection_end: this.selectionEnd
      }

      // Add document version ID if available
      if (this.documentVersionIdValue) {
        commentData.document_version_id = this.documentVersionIdValue
      }

      // Fetch rendered comment card from server
      const response = await fetch(`/inloops/${inloopId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(commentData)
      })

      if (response.ok) {
        const data = await response.json()
        const html = data.html
        // Add to comments list
        this.commentsListTarget.insertAdjacentHTML('beforeend', html)

        // Add comment ID to the newly created card
        const cards = this.commentsListTarget.querySelectorAll('.bg-white.border')
        const newCard = cards[cards.length - 1]
        if (newCard && data.comment_id) {
          newCard.dataset.commentId = data.comment_id
        }

        // Update comment counts after creating a new comment
        this.updateCommentCounts()

        return data.comment_id
      } else {
        throw new Error(`Failed to create comment: ${response.statusText}`)
      }
    } catch (error) {
      throw error
    }
  }

  cancelComment(event) {
    event.preventDefault()
    this.hideCommentForm()
  }

  // Helper method to get selected text position for positioning the comment
  getSelectionPosition() {
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const rect = range.getBoundingClientRect()
      return {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right
      }
    }
    return null
  }

  // Calculate character offsets within the document
  getCharacterOffsets(range) {
    if (!this.hasDocumentContentTarget) {
      return { start: 0, end: 0 }
    }

    const documentElement = this.documentContentTarget
    const preRange = document.createRange()
    preRange.selectNodeContents(documentElement)
    preRange.setEnd(range.startContainer, range.startOffset)

    const start = preRange.toString().length
    const end = start + range.toString().length

    return { start, end }
  }

  filterComments(event) {
    const tab = event.currentTarget
    const filterType = tab.dataset.filterType

    // Update active filter
    this.activeFilter = filterType

    // Update tab styles
    this.updateTabStyles(filterType)

    // Apply filter to comments
    if (!this.hasCommentsListTarget) {
      return
    }

    const allComments = this.commentsListTarget.querySelectorAll('[data-comment-id]')

    allComments.forEach(comment => {
      const status = comment.dataset.commentStatus || 'active'

      if (filterType === 'all') {
        // Show all comments
        comment.style.display = ''
      } else if (filterType === 'unresolved') {
        // Show only active/unresolved comments
        if (status === 'active') {
          comment.style.display = ''
        } else {
          comment.style.display = 'none'
        }
      } else if (filterType === 'resolved') {
        // Show only resolved comments
        if (status === 'resolved') {
          comment.style.display = ''
        } else {
          comment.style.display = 'none'
        }
      }
    })
  }

  updateTabStyles(activeFilter) {
    const tabs = [
      { target: this.hasAllTabTarget ? this.allTabTarget : null, type: 'all' },
      { target: this.hasUnresolvedTabTarget ? this.unresolvedTabTarget : null, type: 'unresolved' },
      { target: this.hasResolvedTabTarget ? this.resolvedTabTarget : null, type: 'resolved' }
    ].filter(tab => tab.target !== null)

    tabs.forEach(({ target, type }) => {
      const textElement = target.querySelector('p')
      if (type === activeFilter) {
        // Active tab
        target.classList.remove('hover:bg-gray-100')
        textElement.classList.remove('text-[#6B7280]')
        textElement.classList.add('text-[#1F2937]')
      } else {
        // Inactive tab
        target.classList.add('hover:bg-gray-100')
        textElement.classList.remove('text-[#1F2937]')
        textElement.classList.add('text-[#6B7280]')
      }
    })
  }

  clearFilter() {
    // Show all comments
    if (!this.hasCommentsListTarget) {
      return
    }

    const allComments = this.commentsListTarget.querySelectorAll('[data-comment-id]')
    allComments.forEach(comment => {
      comment.style.display = ''
    })

    // Reset active filter state
    this.activeFilter = 'all'
  }

  filterByType(event) {
    const filterType = event.currentTarget.dataset.filterType

    // Filter comments by type
    if (!this.hasCommentsListTarget) {
      return
    }

    const allComments = this.commentsListTarget.querySelectorAll('[data-comment-id]')

    allComments.forEach(comment => {
      const colorElement = comment.querySelector('[data-color]')
      const commentType = colorElement?.dataset.color?.toLowerCase()

      // Map color to type
      const typeMap = {
        'warning': 'partially_agree',
        'success': 'agree',
        'error': 'disagree'
      }

      const mappedType = typeMap[commentType]

      if (mappedType === filterType) {
        comment.style.display = ''
      } else {
        comment.style.display = 'none'
      }
    })

    // Update active filter state
    this.activeFilter = filterType
  }

  updateCommentCounts() {
    // Count comments by type and status from the DOM
    if (!this.hasCommentsListTarget) {
      return
    }

    const allComments = this.commentsListTarget.querySelectorAll('[data-comment-id]')

    const counts = {
      all: 0,
      unresolved: 0,
      resolved: 0,
      agree: 0,
      partially_agree: 0,
      disagree: 0
    }

    allComments.forEach(comment => {
      counts.all++

      // Count by status (default to unresolved if no status attribute)
      const status = comment.dataset.commentStatus || 'active'
      if (status === 'active') {
        counts.unresolved++
      } else if (status === 'resolved') {
        counts.resolved++
      }

      // Count by type
      const colorElement = comment.querySelector('[data-color]')
      const commentType = colorElement?.dataset.color?.toLowerCase()

      const typeMap = {
        'warning': 'partially_agree',
        'success': 'agree',
        'error': 'disagree'
      }

      const mappedType = typeMap[commentType]
      if (mappedType && counts[mappedType] !== undefined) {
        counts[mappedType]++
      }
    })

    // Tab labels remain static (no counts shown)

    // Update filter dropdown badges
    const filterDropdown = document.querySelector('[data-controller*="dropdown"]')
    if (filterDropdown) {
      // Update Partially agree badge
      const partiallyAgreeItem = filterDropdown.querySelector('[data-filter-type="partially_agree"]')
      if (partiallyAgreeItem) {
        const badgeText = partiallyAgreeItem.querySelector('[data-color="warning"]')
        if (badgeText) {
          badgeText.textContent = `Partially agree (${counts.partially_agree})`
        }
      }

      // Update Agree badge
      const agreeItem = filterDropdown.querySelector('[data-filter-type="agree"]')
      if (agreeItem) {
        const badgeText = agreeItem.querySelector('[data-color="success"]')
        if (badgeText) {
          badgeText.textContent = `Agree (${counts.agree})`
        }
      }

      // Update Disagree badge
      const disagreeItem = filterDropdown.querySelector('[data-filter-type="disagree"]')
      if (disagreeItem) {
        const badgeText = disagreeItem.querySelector('[data-color="error"]')
        if (badgeText) {
          badgeText.textContent = `Disagree (${counts.disagree})`
        }
      }
    }
  }

  async deleteComment(event) {
    event.preventDefault()
    event.stopPropagation()

    const commentCard = event.currentTarget.closest('[data-comment-id]')
    if (!commentCard) {
      return
    }

    const commentId = commentCard.dataset.commentId

    // Confirm deletion
    if (!confirm('Are you sure you want to delete this comment?')) {
      return
    }

    try {
      const inloopId = this.inloopIdValue || this.getInloopIdFromUrl()
      const response = await fetch(`/inloops/${inloopId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to delete comment: ${response.statusText}`)
      }
    } catch (error) {
      throw error
    }

    // Find and remove the associated message button
    const buttonIndex = this.messageButtons.findIndex(btn => btn.commentId === commentId)
    if (buttonIndex !== -1) {
      const buttonData = this.messageButtons[buttonIndex]

      // Remove highlight if it exists
      if (buttonData.highlight && buttonData.highlight.parentNode) {
        const text = buttonData.highlight.textContent
        const textNode = document.createTextNode(text)
        buttonData.highlight.parentNode.replaceChild(textNode, buttonData.highlight)
      }

      // Remove button from DOM
      if (buttonData.button && buttonData.button.parentNode) {
        buttonData.button.parentNode.removeChild(buttonData.button)
      }

      // Remove from array
      this.messageButtons.splice(buttonIndex, 1)
    }

    // Remove comment card from DOM
    commentCard.remove()

    // Update comment counts after deleting
    this.updateCommentCounts()

    // Show empty state if no comments left
    if (this.hasCommentsListTarget && this.hasEmptyStateTarget) {
      const remainingComments = this.commentsListTarget.querySelectorAll('[data-comment-id]')
      if (remainingComments.length === 0) {
        this.emptyStateTarget.classList.remove('hidden')
      }
    }
  }

  editComment(event) {
    event.preventDefault()
    event.stopPropagation()

    const commentCard = event.currentTarget.closest('[data-comment-id]')
    if (!commentCard) {
      return
    }

    const commentId = commentCard.dataset.commentId

    // Get current comment text
    const commentTextElement = commentCard.querySelector('p:last-child')
    const currentText = (commentTextElement?.textContent || '').trim()

    // Create inline edit form
    const editForm = document.createElement('div')
    editForm.className = 'flex flex-col gap-2 w-full'

    // Create textarea separately to avoid whitespace issues
    const textarea = document.createElement('textarea')
    textarea.className = 'w-full px-3 py-2 border border-[#D1D5DB] rounded-lg text-sm leading-5 text-[#030712] resize-none focus:outline-none focus:ring-2 focus:ring-[#7147FF] focus:border-transparent'
    textarea.rows = 3
    textarea.dataset.editTextarea = ''
    textarea.value = currentText

    // Create buttons container
    const buttonsDiv = document.createElement('div')
    buttonsDiv.className = 'flex items-center gap-2 justify-end'
    buttonsDiv.innerHTML = `<button class="px-3 py-1.5 text-sm font-medium text-[#6B7280] hover:text-[#030712] rounded-md hover:bg-gray-100" data-action="click->comment#cancelEdit">Cancel</button><button class="px-3 py-1.5 text-sm font-medium text-white bg-[#7147FF] hover:bg-[#5B3ACC] rounded-md" data-action="click->comment#saveEdit">Save</button>`

    editForm.appendChild(textarea)
    editForm.appendChild(buttonsDiv)

    // Replace comment text with edit form
    const commentContent = commentCard.querySelector('.flex.flex-col.gap-1')
    if (commentContent) {
      // Store original content and comment ID for later
      editForm.dataset.commentId = commentId
      editForm.dataset.originalContent = commentContent.outerHTML

      commentContent.replaceWith(editForm)

      // Focus on textarea (using the reference we already have)
      textarea.focus()
      textarea.setSelectionRange(currentText.length, currentText.length)
    }
  }

  cancelEdit(event) {
    event.preventDefault()
    event.stopPropagation()

    const editForm = event.currentTarget.closest('[data-comment-id]')
    if (!editForm) return

    const originalContent = editForm.dataset.originalContent
    if (originalContent) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = originalContent
      editForm.replaceWith(tempDiv.firstElementChild)
    }
  }

  async saveEdit(event) {
    event.preventDefault()
    event.stopPropagation()

    const editForm = event.currentTarget.closest('[data-comment-id]')
    if (!editForm) return

    const textarea = editForm.querySelector('[data-edit-textarea]')
    const newText = textarea?.value.trim()

    if (!newText) {
      alert('Comment text cannot be empty')
      return
    }

    const commentId = editForm.dataset.commentId

    try {
      const inloopId = this.inloopIdValue || this.getInloopIdFromUrl()
      const response = await fetch(`/inloops/${inloopId}/comments/${commentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ comment_text: newText })
      })

      if (!response.ok) {
        throw new Error(`Failed to update comment: ${response.statusText}`)
      }
    } catch (error) {
      throw error
    }

    // Restore original structure with updated text
    const originalContent = editForm.dataset.originalContent
    if (originalContent) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = originalContent

      // Update the comment text in the restored content
      const commentTextElement = tempDiv.querySelector('p:last-child')
      if (commentTextElement) {
        commentTextElement.textContent = newText
      }

      editForm.replaceWith(tempDiv.firstElementChild)
    }
  }

  async resolveComment(event) {
    event.preventDefault()
    event.stopPropagation()

    const commentCard = event.currentTarget.closest('[data-comment-id]')
    if (!commentCard) {
      return
    }

    // Confirm before resolving
    if (!confirm('Are you sure you want to mark this comment as resolved?')) {
      return
    }

    const commentId = commentCard.dataset.commentId
    // Store reference to the button before async operation
    const resolveButton = event.currentTarget

    try {
      const inloopId = this.inloopIdValue || this.getInloopIdFromUrl()
      const response = await fetch(`/inloops/${inloopId}/comments/${commentId}/resolve`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error || response.statusText
        throw new Error(`Failed to resolve comment: ${errorMessage}`)
      }

      // Update comment status in DOM
      commentCard.dataset.commentStatus = 'resolved'

      // Add "Resolved" badge after comment type badge
      const badgesContainer = commentCard.querySelector('.flex.items-center.gap-2')
      if (badgesContainer && !commentCard.querySelector('[data-resolved-badge]')) {
        const resolvedBadgeHTML = `
          <span data-resolved-badge>
            <span class="inline-flex items-center justify-center rounded-full font-medium border h-[22px] px-2 text-xs gap-1 bg-[#F2F4F7] border-[#E4E7EC] text-[#344054]">
              Resolved
            </span>
          </span>
        `
        badgesContainer.insertAdjacentHTML('beforeend', resolvedBadgeHTML)
      }

      // Hide the resolve button after resolving
      if (resolveButton) {
        resolveButton.style.display = 'none'
      }

      // Update comment counts after resolving
      this.updateCommentCounts()

      // Apply current filter to show/hide based on new status
      if (this.activeFilter === 'unresolved') {
        commentCard.style.display = 'none'
      } else if (this.activeFilter === 'resolved') {
        commentCard.style.display = ''
      }

    } catch (error) {
      console.error('Error resolving comment:', error)
      alert(error.message || 'Failed to resolve comment. Please try again.')
    }
  }

  // Highlight the selected text with brand colors (only called when message button is clicked)
  highlightSelection() {
    if (!this.currentRange) return

    try {
      // Remove existing highlight if any
      if (this.currentHighlight && this.currentHighlight.parentNode) {
        const text = this.currentHighlight.textContent
        const textNode = document.createTextNode(text)
        this.currentHighlight.parentNode.replaceChild(textNode, this.currentHighlight)
        this.currentHighlight = null
      }

      // Create a span element for highlighting
      const highlightSpan = document.createElement('span')
      highlightSpan.className = 'comment-highlight-temp'
      highlightSpan.style.cssText = `
        background-color: #F5F5FF;
        border: 1px solid #B1B1FF;
        border-radius: 8px;
        padding: 2px 4px;
        display: inline;
      `

      // Clone the range to avoid modifying the original
      const range = this.currentRange.cloneRange()

      // Wrap the selected content with the highlight span
      // Use extractContents + appendChild for more robust handling
      try {
        range.surroundContents(highlightSpan)
      } catch (e) {
        // If surroundContents fails (e.g., range spans multiple elements),
        // use extractContents instead
        const contents = range.extractContents()
        highlightSpan.appendChild(contents)
        range.insertNode(highlightSpan)
      }

      // Store reference to the highlight
      this.currentHighlight = highlightSpan

      // Clear browser selection
      window.getSelection().removeAllRanges()
    } catch (error) {
      console.error('Error highlighting selection:', error)
      throw error
    }
  }

  // Remove the highlight from selected text
  removeHighlight() {
    // Clear the current range if it exists
    if (this.currentRange) {
      this.currentRange = null
    }

    if (this.currentHighlight && this.currentHighlight.parentNode) {
      // Get the text content
      const text = this.currentHighlight.textContent
      const textNode = document.createTextNode(text)

      // Replace the highlight span with plain text
      this.currentHighlight.parentNode.replaceChild(textNode, this.currentHighlight)

      // Save the text node for calculating message button position later
      this.savedTextNode = textNode

      this.currentHighlight = null
    }
  }

  // Show message button at end of line with numbering
  showMessageButton() {
    if (!this.savedTextNode || !this.savedSelectedText || !this.commentId) {
      return
    }

    try {
      // Get margin column
      const marginColumn = this.hasMarginColumnTarget ? this.marginColumnTarget : null

      if (!marginColumn) {
        return
      }

      // Get the position of the selected line for vertical alignment
      const endOfLine = this.getEndOfLinePosition(this.savedTextNode)
      if (!endOfLine) {
        return
      }

      // Simple sequential button number
      const buttonNumber = this.messageButtons.length + 1

      // Create the message button (optimized for margin column)
      const button = document.createElement('button')
      button.className = 'message-button'
      button.dataset.commentId = this.commentId
      button.dataset.buttonNumber = buttonNumber

      // Create SVG icon wrapper (smaller, cleaner design for margin)
      const iconWrapper = document.createElement('div')
      iconWrapper.style.cssText = 'width: 16px; height: 16px; position: relative; display: flex; align-items: center; justify-content: center;'
      iconWrapper.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 8.5H12M7 12H15M9.68375 18H16.2C17.8802 18 18.7202 18 19.362 17.673C19.9265 17.3854 20.3854 16.9265 20.673 16.362C21 15.7202 21 14.8802 21 13.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V20.3355C3 20.8684 3 21.1348 3.10923 21.2716C3.20422 21.3906 3.34827 21.4599 3.50054 21.4597C3.67563 21.4595 3.88367 21.2931 4.29976 20.9602L6.68521 19.0518C7.17252 18.662 7.41617 18.4671 7.68749 18.3285C7.9282 18.2055 8.18443 18.1156 8.44921 18.0613C8.74767 18 9.0597 18 9.68375 18Z" stroke="#7147FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
      button.appendChild(iconWrapper)

      // Style the button (absolute positioning in margin column, aligned with text line)
      button.style.cssText = `
        position: absolute;
        width: 32px;
        height: 32px;
        padding: 8px;
        background: #F5F3FF;
        border: 1.5px solid #E9E5FF;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.15s ease;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      `

      // Calculate position in margin column aligned with the text line
      const marginRect = marginColumn.getBoundingClientRect()
      let topPos = endOfLine.top - marginRect.top
      const leftPos = 8 // Small padding from left edge of margin column

      // Check for collision and adjust position if needed
      const buttonHeight = 32 // Button height
      const minSpacing = 4 // Minimum spacing between buttons
      topPos = this.findNonCollidingPosition(topPos, buttonHeight, minSpacing)

      button.style.top = `${topPos}px`
      button.style.left = `${leftPos}px`

      // Track this position as occupied
      this.occupiedPositions.push({
        top: topPos,
        bottom: topPos + buttonHeight
      })

      // Store reference data for this button
      const buttonData = {
        button: button,
        commentId: this.commentId,
        textNode: this.savedTextNode,
        selectedText: this.savedSelectedText,
        highlight: null, // Will store the highlight span when toggled on
        range: this.currentRange ? this.currentRange.cloneRange() : null, // Store range for highlighting
        buttonNumber: buttonNumber
      }

      // Add hover effect (updated for new design)
      button.addEventListener('mouseenter', () => {
        if (!buttonData.highlight) {
          button.style.background = '#EDE9FE'
          button.style.borderColor = '#DDD6FE'
          button.style.transform = 'scale(1.05)'
        }
      })

      button.addEventListener('mouseleave', () => {
        if (!buttonData.highlight) {
          button.style.background = '#F5F3FF'
          button.style.borderColor = '#E9E5FF'
          button.style.transform = 'scale(1)'
        }
      })

      // Add click handler to toggle highlight
      button.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()

        // Toggle highlight on/off for this specific comment
        if (buttonData.highlight) {
          // Highlight is currently shown, remove it
          this.removeSpecificHighlight(buttonData)
          // Update button style to default state
          button.style.background = '#F5F3FF'
          button.style.borderColor = '#E9E5FF'
          button.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
          // Update icon color to brand purple
          const svg = iconWrapper.querySelector('svg path')
          if (svg) svg.setAttribute('stroke', '#7147FF')
        } else {
          // Highlight is hidden, show it
          this.showSpecificHighlight(buttonData)
          // Update button style to active state
          button.style.background = '#7147FF'
          button.style.borderColor = '#7147FF'
          button.style.boxShadow = '0 2px 4px rgba(113, 71, 255, 0.2)'
          // Update icon color to white
          const svg = iconWrapper.querySelector('svg path')
          if (svg) svg.setAttribute('stroke', '#FFFFFF')
        }

        // Scroll to comment
        this.scrollToComment(this.commentId)
      })

      // Add to margin column
      marginColumn.appendChild(button)

      // Store in array
      this.messageButtons.push(buttonData)

    } catch (error) {
      throw error
    }
  }

  // Get the end position of the line where the text node is located
  getEndOfLinePosition(textNode) {
    try {
      // Find the parent element containing the text
      let parentElement = textNode.parentElement
      if (!parentElement) {
        return null
      }

      // Get the bounding rect of the text node to determine which line it's on
      const textRange = document.createRange()
      textRange.selectNodeContents(textNode)
      const textRect = textRange.getBoundingClientRect()
      const textTop = textRect.top
      const textBottom = textRect.bottom

      // Find the end of the line by walking through all text nodes in the parent
      let lineEndPosition = { right: textRect.right, top: textTop, bottom: textBottom }

      // Helper function to recursively find all text nodes
      const getTextNodes = (node) => {
        const textNodes = []
        if (node.nodeType === Node.TEXT_NODE) {
          textNodes.push(node)
        } else {
          for (const child of node.childNodes) {
            textNodes.push(...getTextNodes(child))
          }
        }
        return textNodes
      }

      // Get all text nodes in the parent element
      const allTextNodes = getTextNodes(parentElement)

      // Check each text node to find ones on the same line
      for (const node of allTextNodes) {
        if (node.textContent.trim().length === 0) continue

        try {
          const nodeRange = document.createRange()
          nodeRange.selectNodeContents(node)
          const nodeRect = nodeRange.getBoundingClientRect()

          // Check if this node is on the same line (within 2px tolerance)
          if (nodeRect.top >= textTop - 2 && nodeRect.top <= textBottom + 2) {
            // Update line end position if this node extends further right
            if (nodeRect.right > lineEndPosition.right) {
              lineEndPosition = {
                right: nodeRect.right,
                top: nodeRect.top,
                bottom: nodeRect.bottom
              }
            }
          }
        } catch (err) {
          // Skip nodes that can't be measured
          continue
        }
      }

      return lineEndPosition
    } catch (error) {
      throw error
    }
  }

  // Get the end position of the first line of selection
  getEndOfFirstLine(range) {
    try {
      // Get all client rects (one for each line in multi-line selection)
      const rects = range.getClientRects()

      if (rects.length > 0) {
        // Return the first rect (first line)
        return rects[0]
      }

      // Fallback to main rect
      return range.getBoundingClientRect()
    } catch (error) {
      throw error
    }
  }

  // Show highlight for a specific button/comment
  showSpecificHighlight(buttonData) {
    try {
      // Find the text in the entire document (text may span multiple nodes)
      if (!this.hasDocumentContentTarget) {
        return
      }

      const docText = this.documentContentTarget.textContent
      const selectedText = buttonData.selectedText
      const foundIndex = docText.indexOf(selectedText)

      if (foundIndex === -1) {
        return
      }

      // Create range using found position (may span multiple text nodes)
      const range = this.createRangeFromOffsets(foundIndex, foundIndex + selectedText.length)

      if (!range) {
        return
      }

      // Validate range before proceeding
      if (!range || range.collapsed) {
        return
      }

      // Create a span element for highlighting
      const highlightSpan = document.createElement('span')
      highlightSpan.className = 'comment-highlight'
      highlightSpan.dataset.commentId = buttonData.commentId
      highlightSpan.style.cssText = `
        background-color: #F5F5FF;
        border: 1px solid #B1B1FF;
        border-radius: 8px;
        padding: 2px 4px;
        display: inline;
      `

      // Try to wrap the selected content with the highlight span
      // Use extractContents + appendChild for more robust handling of complex ranges
      try {
        range.surroundContents(highlightSpan)
      } catch (e) {
        // If surroundContents fails (e.g., range spans multiple elements),
        // use extractContents instead
        const contents = range.extractContents()
        highlightSpan.appendChild(contents)
        range.insertNode(highlightSpan)
      }

      // Update button data with the new highlight
      buttonData.highlight = highlightSpan

      // Update text node reference - use firstChild if it's a text node, otherwise traverse
      if (highlightSpan.firstChild && highlightSpan.firstChild.nodeType === Node.TEXT_NODE) {
        buttonData.textNode = highlightSpan.firstChild
      } else {
        // Find first text node in highlight span
        const walker = document.createTreeWalker(
          highlightSpan,
          NodeFilter.SHOW_TEXT,
          null,
          false
        )
        const firstTextNode = walker.nextNode()
        if (firstTextNode) {
          buttonData.textNode = firstTextNode
        }
      }

      // Highlight corresponding comment card
      this.highlightSpecificCommentCard(buttonData.commentId)

    } catch (error) {
      // Silent fail
    }
  }

  // Remove highlight for a specific button/comment
  removeSpecificHighlight(buttonData) {
    if (buttonData.highlight && buttonData.highlight.parentNode) {
      // Get the text content
      const text = buttonData.highlight.textContent
      const textNode = document.createTextNode(text)

      // Replace the highlight span with plain text
      buttonData.highlight.parentNode.replaceChild(textNode, buttonData.highlight)

      // Update button data
      buttonData.textNode = textNode
      buttonData.highlight = null

      // Remove highlight from comment card
      this.removeSpecificCommentCardHighlight(buttonData.commentId)

    }
  }

  // Highlight a specific comment card by ID
  highlightSpecificCommentCard(commentId) {
    if (!commentId || !this.hasCommentsListTarget) return

    const commentCard = this.commentsListTarget.querySelector(`[data-comment-id="${commentId}"]`)
    if (commentCard) {
      commentCard.style.border = '2px solid #B1B1FF'
      commentCard.style.boxShadow = '0px 0px 0px 4px rgba(177, 177, 255, 0.1)'
    }
  }

  // Remove highlight from a specific comment card by ID
  removeSpecificCommentCardHighlight(commentId) {
    if (!commentId || !this.hasCommentsListTarget) return

    const commentCard = this.commentsListTarget.querySelector(`[data-comment-id="${commentId}"]`)
    if (commentCard) {
      commentCard.style.border = '1px solid #F2F3F5'
      commentCard.style.boxShadow = ''
    }
  }

  // Remove all message buttons
  removeAllMessageButtons() {
    this.messageButtons.forEach(buttonData => {
      if (buttonData.button && buttonData.button.parentNode) {
        buttonData.button.parentNode.removeChild(buttonData.button)
      }
    })
    this.messageButtons = []
  }

  // Highlight the corresponding comment card (legacy - for current selection)
  highlightCommentCard() {
    if (!this.commentId || !this.hasCommentsListTarget) return

    // Find the comment card with matching ID
    const commentCard = this.commentsListTarget.querySelector(`[data-comment-id="${this.commentId}"]`)
    if (commentCard) {
      // Add highlight border
      commentCard.style.border = '2px solid #B1B1FF'
      commentCard.style.boxShadow = '0px 0px 0px 4px rgba(177, 177, 255, 0.1)'
    }
  }

  // Remove highlight from comment card (legacy - for current selection)
  removeCommentCardHighlight() {
    if (!this.commentId || !this.hasCommentsListTarget) return

    // Find the comment card with matching ID
    const commentCard = this.commentsListTarget.querySelector(`[data-comment-id="${this.commentId}"]`)
    if (commentCard) {
      // Remove highlight border
      commentCard.style.border = '1px solid #F2F3F5'
      commentCard.style.boxShadow = ''
    }
  }

  // Helper to extract inloop ID from URL
  // Supports paths like: /facilitator/inloops/:id/... or /inloops/:id/...
  getInloopIdFromUrl() {
    const pathParts = window.location.pathname.split('/')
    const inloopsIndex = pathParts.findIndex(part => part === 'inloops')
    if (inloopsIndex !== -1 && pathParts[inloopsIndex + 1]) {
      return pathParts[inloopsIndex + 1]
    }
    return '1' // fallback
  }

  // Restore highlights for existing comments when page loads
  restoreExistingCommentHighlights() {
    if (!this.hasCommentsListTarget || !this.hasDocumentContentTarget) {
      return
    }

    const docText = this.documentContentTarget.textContent
    const commentCards = this.commentsListTarget.querySelectorAll('[data-selection-start][data-selection-end]')

    commentCards.forEach((card) => {
      const commentId = card.dataset.commentId
      const selectionStart = parseInt(card.dataset.selectionStart, 10)
      const selectionEnd = parseInt(card.dataset.selectionEnd, 10)
      const selectedText = card.dataset.selectedText || ''

      if (!commentId || !selectedText) {
        return
      }

      // Create highlight and message button for this comment
      try {
        let range = null

        // Primary method: Find by exact text content (most reliable)
        const foundIndex = docText.indexOf(selectedText)

        if (foundIndex !== -1) {
          // Create range using found position
          range = this.createRangeFromOffsets(foundIndex, foundIndex + selectedText.length)

          // Verify the range contains the correct text
          if (range && range.toString() === selectedText) {
            this.createHighlightForExistingComment(range, commentId, selectedText)
          }
        } else if (!isNaN(selectionStart) && !isNaN(selectionEnd)) {
          // Fallback: Try using stored offsets if text search fails
          range = this.createRangeFromOffsets(selectionStart, selectionEnd)

          if (range) {
            this.createHighlightForExistingComment(range, commentId, selectedText)
          }
        }
      } catch (error) {
        // Silently skip comments that can't be restored
      }
    })
  }

  // Create a Range object from character offsets
  createRangeFromOffsets(start, end) {
    if (!this.hasDocumentContentTarget) {
      return null
    }

    const documentElement = this.documentContentTarget
    let currentOffset = 0
    let startNode = null
    let startOffset = 0
    let endNode = null
    let endOffset = 0

    // Walk through all text nodes to find start and end positions
    // NOTE: This MUST match the logic in getCharacterOffsets()
    const walker = document.createTreeWalker(
      documentElement,
      NodeFilter.SHOW_TEXT,
      null,
      false
    )

    let node
    while (node = walker.nextNode()) {
      const nodeLength = node.textContent.length

      // DON'T skip empty nodes - to stay consistent with getCharacterOffsets
      // which uses toString() that includes all text nodes

      // Find start position
      if (!startNode && currentOffset + nodeLength > start) {
        startNode = node
        startOffset = start - currentOffset
      }

      // Find end position
      if (!endNode && currentOffset + nodeLength >= end) {
        endNode = node
        endOffset = end - currentOffset
        break
      }

      currentOffset += nodeLength
    }

    if (!startNode || !endNode) {
      return null
    }

    // Ensure offsets are within bounds
    startOffset = Math.max(0, Math.min(startOffset, startNode.textContent.length))
    endOffset = Math.max(0, Math.min(endOffset, endNode.textContent.length))

    const range = document.createRange()
    try {
      range.setStart(startNode, startOffset)
      range.setEnd(endNode, endOffset)
      return range
    } catch (error) {
      return null
    }
  }

  // Create highlight and message button for an existing comment
  createHighlightForExistingComment(range, commentId, selectedText) {
    try {
      // Get the text node from the range without creating a visible highlight
      const startNode = range.startContainer
      let textNode = startNode

      if (startNode.nodeType === Node.TEXT_NODE) {
        textNode = startNode
      } else if (startNode.firstChild && startNode.firstChild.nodeType === Node.TEXT_NODE) {
        textNode = startNode.firstChild
      }

      // Create and show message button without visible highlight
      const messageButton = this.createMessageButton(textNode, commentId, selectedText, range)
      if (messageButton) {
        this.messageButtons.push({
          button: messageButton,
          commentId: commentId,
          textNode: textNode,
          selectedText: selectedText,
          highlight: null, // No permanent highlight
          range: range.cloneRange() // Store range for later use when button is clicked
        })
      }
    } catch (error) {
      throw error
    }
  }

  // Scroll to a specific comment in the sidebar
  scrollToComment(commentId) {
    if (!this.hasCommentsListTarget) return

    const commentCard = this.commentsListTarget.querySelector(`[data-comment-id="${commentId}"]`)
    if (commentCard) {
      commentCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  // Create message button for a comment (used for restoring existing comments)
  createMessageButton(textNode, commentId, selectedText, range) {
    try {
      // Get margin column
      const marginColumn = this.hasMarginColumnTarget ? this.marginColumnTarget : null

      if (!marginColumn) {
        return null
      }

      // Get the position of the selected line for vertical alignment
      const endOfLine = this.getEndOfLinePosition(textNode)
      if (!endOfLine) {
        return null
      }

      // Simple sequential button number
      const buttonNumber = this.messageButtons.length + 1

      // Create the message button
      const button = document.createElement('button')
      button.className = 'message-button'
      button.dataset.commentId = commentId
      button.dataset.buttonNumber = buttonNumber

      // Create SVG icon wrapper (smaller, cleaner design for margin)
      const iconWrapper = document.createElement('div')
      iconWrapper.style.cssText = 'width: 16px; height: 16px; position: relative; display: flex; align-items: center; justify-content: center;'
      iconWrapper.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 8.5H12M7 12H15M9.68375 18H16.2C17.8802 18 18.7202 18 19.362 17.673C19.9265 17.3854 20.3854 16.9265 20.673 16.362C21 15.7202 21 14.8802 21 13.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V20.3355C3 20.8684 3 21.1348 3.10923 21.2716C3.20422 21.3906 3.34827 21.4599 3.50054 21.4597C3.67563 21.4595 3.88367 21.2931 4.29976 20.9602L6.68521 19.0518C7.17252 18.662 7.41617 18.4671 7.68749 18.3285C7.9282 18.2055 8.18443 18.1156 8.44921 18.0613C8.74767 18 9.0597 18 9.68375 18Z" stroke="#7147FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
      button.appendChild(iconWrapper)

      // Style the button (absolute positioning in margin column, aligned with text line)
      button.style.cssText = `
        position: absolute;
        width: 32px;
        height: 32px;
        padding: 8px;
        background: #F5F3FF;
        border: 1.5px solid #E9E5FF;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.15s ease;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      `

      // Calculate position in margin column aligned with the text line
      const marginRect = marginColumn.getBoundingClientRect()
      let topPos = endOfLine.top - marginRect.top
      const leftPos = 8 // Small padding from left edge of margin column

      // Check for collision and adjust position if needed
      const buttonHeight = 32 // Button height
      const minSpacing = 4 // Minimum spacing between buttons
      topPos = this.findNonCollidingPosition(topPos, buttonHeight, minSpacing)

      button.style.top = `${topPos}px`
      button.style.left = `${leftPos}px`

      // Track this position as occupied
      this.occupiedPositions.push({
        top: topPos,
        bottom: topPos + buttonHeight
      })

      // Store button data for later access
      const buttonData = {
        button: button,
        commentId: commentId,
        textNode: textNode,
        selectedText: selectedText,
        highlight: null,
        range: range ? range.cloneRange() : null,
        buttonNumber: buttonNumber
      }

      // Add click handler to toggle highlight
      button.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()

        // Toggle highlight on/off
        if (buttonData.highlight) {
          // Remove highlight
          this.removeSpecificHighlight(buttonData)
          button.style.background = '#F5F3FF'
          button.style.borderColor = '#E9E5FF'
          button.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
          const svg = iconWrapper.querySelector('svg path')
          if (svg) svg.setAttribute('stroke', '#7147FF')
        } else {
          // Show highlight
          this.showSpecificHighlight(buttonData)
          button.style.background = '#7147FF'
          button.style.borderColor = '#7147FF'
          button.style.boxShadow = '0 2px 4px rgba(113, 71, 255, 0.2)'
          const svg = iconWrapper.querySelector('svg path')
          if (svg) svg.setAttribute('stroke', '#FFFFFF')
        }

        // Scroll to comment
        this.scrollToComment(commentId)
      })

      // Add hover effect (updated for new design)
      button.addEventListener('mouseenter', () => {
        if (!buttonData.highlight) {
          button.style.background = '#EDE9FE'
          button.style.borderColor = '#DDD6FE'
          button.style.transform = 'scale(1.05)'
        }
      })

      button.addEventListener('mouseleave', () => {
        if (!buttonData.highlight) {
          button.style.background = '#F5F3FF'
          button.style.borderColor = '#E9E5FF'
          button.style.transform = 'scale(1)'
        }
      })

      // Add to margin column
      marginColumn.appendChild(button)

      return button
    } catch (error) {
      throw error
    }
  }
}
